import React, { useState } from 'react'
import TextField from '@material-ui/core/TextField'
import ToggleButton from '@material-ui/lab/ToggleButton'
import ToggleButtonGroup from '@material-ui/lab/ToggleButtonGroup'
import AttachMoneyIcon from '@material-ui/icons/AttachMoney'
import PropTypes from 'prop-types'
import {
  AddButtonContainer,
  DiscountCard,
  DiscountEquals,
  DiscountFormula,
  DiscountLabel,
  DiscountLabelStyled,
  DiscountLine,
  DiscountRow,
  DiscountSectionWrapper,
  DiscountTotal,
  DiscountValue,
  DollarSymbol,
  PercentSymbol,
  SectionTitle,
} from './Discounts.styles'
import { StyledMutedButton } from '../Approvals/ApprovalView.styles'
import { updateProjectInvoice } from '../../entities/project/service'
import { getDiscountedTotal } from './utils'

const symbolPerType = {
  percentage: <PercentSymbol>%</PercentSymbol>,
  fixed: <DollarSymbol>$</DollarSymbol>,
}

function CurrencyToggle({ isPercent, setIsPercent }) {
  const handleChange = (event, newValue) => {
    if (newValue !== null) {
      setIsPercent(newValue === 'percent')
    }
  }

  return (
    <ToggleButtonGroup
      value={isPercent ? 'percent' : 'fixed'}
      exclusive
      onChange={(e, value) => {
        if (value !== null) setIsPercent(value === 'percent')
      }}
    >
      <ToggleButton value="fixed">$</ToggleButton>
      <ToggleButton value="percent">%</ToggleButton>
    </ToggleButtonGroup>
  )
}

const DiscountCardComponent = ({ discount, initialTotal, extraDiscounts = [] }) => {
  if (!discount || discount.type === 'none') return null

  let discountValue = 0
  if (discount.type === 'percentage') {
    discountValue = (discount.value / 100) * initialTotal
  } else if (discount.type === 'fixed') {
    discountValue = discount.value
  }

  const discountedTotal =
    initialTotal - discountValue - extraDiscounts.reduce((acc, disc) => acc + disc, 0)

  return (
    <DiscountRow>
      <DiscountCard>
        <DiscountLine>
          <DiscountLabelStyled>Discount</DiscountLabelStyled>
          <DiscountValue>
            {symbolPerType[discount.type]} {discount.value}
          </DiscountValue>
        </DiscountLine>
        <DiscountLine>
          <DiscountFormula>
            {initialTotal.toFixed(2)} {extraDiscounts.map(disc => `- ${disc.toFixed(2)}`)} -
            <b> {discountValue.toFixed(2)}</b>
          </DiscountFormula>
          <DiscountEquals>=</DiscountEquals>
          <DiscountTotal>{discountedTotal.toFixed(2)}</DiscountTotal>
        </DiscountLine>
      </DiscountCard>
    </DiscountRow>
  )
}

const DiscountSection = ({ invoice, invoiceValues, client, updateInvoices }) => {
  const [discountValue, setDiscountValue] = useState(0)
  const [isPercent, setIsPercent] = useState(true)

  if (!invoice) {
    return (
      <DiscountSectionWrapper>
        <SectionTitle>No invoice selected</SectionTitle>
      </DiscountSectionWrapper>
    )
  }

  const onChangeCurrency = (event, newValue) => {
    if (newValue !== null) {
      setIsPercent(newValue === 'percent')
    }
  }

  const { finalTotal: totalAfterProjectDiscount } = getDiscountedTotal({
    total: invoiceValues.total,
    projectDiscount: invoice.projectDiscount,
  })

  const handleAddDiscount = () => {
    updateProjectInvoice({
      client,
      invoiceId: invoice._id,
      invoiceData: {
        invoiceDiscount: {
          type: isPercent ? 'percentage' : 'fixed',
          value: +discountValue,
        },
      },
    }).then(() => {
      setDiscountValue(0)
      setIsPercent(true)
      updateInvoices(invoice._id, {
        invoiceDiscount: {
          type: isPercent ? 'percentage' : 'fixed',
          value: +discountValue,
        },
      })
    })
  }

  return (
    <>
      <DiscountSectionWrapper>
        <SectionTitle>Discounts - Project Level</SectionTitle>
        <DiscountCardComponent
          discount={invoice.projectDiscount}
          initialTotal={invoiceValues.total}
        />
      </DiscountSectionWrapper>

      <DiscountSectionWrapper>
        <SectionTitle>Discounts - Invoice Level</SectionTitle>
        <DiscountRow>
          <DiscountLabel>Discount</DiscountLabel>
          <TextField
            variant="outlined"
            size="small"
            type="number"
            value={discountValue}
            onChange={e => setDiscountValue(e.target.value)}
          />
          <CurrencyToggle isPercent={isPercent} setIsPercent={onChangeCurrency} />
        </DiscountRow>
        <DiscountCardComponent
          discount={invoice.invoiceDiscount}
          initialTotal={invoiceValues.total}
          extraDiscounts={[invoiceValues.total - totalAfterProjectDiscount]}
        />
        <AddButtonContainer>
          <StyledMutedButton onClick={handleAddDiscount}>
            {!invoice.invoiceDiscount || invoice.invoiceDiscount.type === 'none' ? 'Add' : 'Update'}
          </StyledMutedButton>
        </AddButtonContainer>
      </DiscountSectionWrapper>
    </>
  )
}

CurrencyToggle.propTypes = {
  isPercent: PropTypes.bool.isRequired,
  setIsPercent: PropTypes.func.isRequired,
}

DiscountSection.propTypes = {
  invoice: PropTypes.shape({
    _id: PropTypes.string,
    projectDiscount: PropTypes.shape({
      type: PropTypes.oneOf(['percentage', 'fixed', 'none']),
      value: PropTypes.number,
    }),
    invoiceDiscount: PropTypes.shape({
      type: PropTypes.oneOf(['percentage', 'fixed', 'none']),
      value: PropTypes.number,
    }),
  }),
  invoiceValues: PropTypes.shape({
    total: PropTypes.number.isRequired,
    discount: PropTypes.number,
  }).isRequired,
}

DiscountCardComponent.propTypes = {
  discount: PropTypes.shape({
    type: PropTypes.oneOf(['percentage', 'fixed', 'none']),
    value: PropTypes.number,
  }).isRequired,
  initialTotal: PropTypes.number.isRequired,
}
export default DiscountSection
